import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Check, FileText, Clock, Monitor, RefreshCw, ShieldCheck, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/site/Nav";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/Seo";
import { PLAN_CONTENT, PLAN_DETAILS } from "@/data/plans";
import { startCheckout, claimFreePlan } from "@/lib/checkout";
import { API } from "@/lib/api";

function BuyButton({ plan, testid }) {
  const [loading, setLoading] = useState(false);
  const buy = async () => {
    setLoading(true);
    try { await startCheckout(plan.lookup_key); }
    catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || "Kunne ikke starte betaling. Prøv igjen.");
    }
  };
  return (
    <button onClick={buy} disabled={loading} data-testid={testid}
      className="inline-flex items-center gap-2 rounded-md bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors disabled:opacity-60">
      <Download size={17} /> {loading ? "Sender til betaling…" : `Kjøp og last ned · ${plan.price}`}
    </button>
  );
}

function FreeClaimButton({ plan, testid, freeToken, setFreeToken }) {
  const [step, setStep] = useState("idle"); // idle | form
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const t = await claimFreePlan(plan.lookup_key, email);
      setFreeToken(t);
      toast.success("Ferdig! Planen er sendt til e-posten din.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke hente planen. Prøv igjen.");
    } finally { setLoading(false); }
  };

  if (freeToken) {
    return (
      <a href={`${API}/download/${freeToken}`} data-testid={`${testid}-download`}
        className="inline-flex items-center gap-2 rounded-md bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors">
        <Download size={17} /> Last ned planen
      </a>
    );
  }

  if (step === "form") {
    return (
      <form onSubmit={submit} className="flex w-full max-w-md flex-col sm:flex-row gap-2.5" data-testid={`${testid}-form`}>
        <div className="relative flex-1">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-olive/40" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Din e-post" data-testid={`${testid}-email`}
            className="w-full rounded-md border border-olive/25 bg-cream pl-9 pr-3 py-3.5 text-[14px] text-olive-deep outline-none focus:border-terra" />
        </div>
        <button type="submit" disabled={loading} data-testid={`${testid}-submit`}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-terra px-7 py-3.5 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors disabled:opacity-60 whitespace-nowrap">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {loading ? "Sender…" : "Send meg planen"}
        </button>
      </form>
    );
  }

  return (
    <button onClick={() => setStep("form")} data-testid={testid}
      className="inline-flex items-center gap-2 rounded-md bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors">
      <Download size={17} /> Last ned gratis
    </button>
  );
}

function Cta({ plan, testid, freeToken, setFreeToken }) {
  return plan.free
    ? <FreeClaimButton plan={plan} testid={testid} freeToken={freeToken} setFreeToken={setFreeToken} />
    : <BuyButton plan={plan} testid={testid} />;
}

export default function PlanDetail() {
  const { slug } = useParams();
  const plan = PLAN_CONTENT[slug];
  const details = PLAN_DETAILS[slug];
  const [freeToken, setFreeToken] = useState(null);

  if (!plan) {
    return (
      <div className="min-h-screen bg-cream text-olive-deep">
        <Nav />
        <Seo title="Kommer snart – Helserebellen" description="Denne planen er under utvikling." path="/plan" noindex />
        <div className="min-h-screen grid place-items-center px-6 text-center">
          <div>
            <p className="font-script text-4xl text-terra">Kommer snart</p>
            <h1 className="mt-3 font-serif text-4xl">Denne planen er under utvikling.</h1>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-md bg-terra px-8 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors">
              <ArrowLeft size={16} /> Til forsiden
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-cream text-olive-deep overflow-clip">
      <Nav />
      <Seo title={`${plan.name} – Helserebellen`} description={plan.title.join(" ")} path={`/plan/${slug}`} image={plan.image} />

      <section className="pt-[110px] md:pt-[130px] pb-8 px-5 md:px-10">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <Link to="/#velg" className="inline-flex items-center gap-2 text-olive/60 text-sm hover:text-terra mb-8"><ArrowLeft size={16} /> Alle planer</Link>
            <p className="text-[12px] font-semibold uppercase tracking-[0.3em] text-terra">{plan.kicker}</p>
            <h1 className="mt-5 font-serif text-3xl md:text-5xl font-medium leading-[1.08] text-olive-deep text-balance">
              {plan.title.map((line, i) => (<span key={i} className="block">{line}</span>))}
            </h1>
            {plan.signature && (
              <p className="mt-5 font-script text-3xl md:text-4xl text-terra" data-testid="plan-signature">{plan.signature}</p>
            )}
            <div className="mt-8 flex flex-col items-center gap-3">
              <Cta plan={plan} testid="plan-buy-top" freeToken={freeToken} setFreeToken={setFreeToken} />
              <span className="text-[12px] uppercase tracking-[0.16em] text-olive/50">
                {plan.free ? "Helt gratis · ingen konto nødvendig" : "Ingen konto nødvendig · sikker betaling med Stripe"}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 md:px-10">
        <Reveal className="mx-auto max-w-4xl">
          <div className="rounded-2xl overflow-hidden aspect-[16/9] shadow-xl bg-cream-deep/40">
            <img src={plan.image} alt={plan.name} className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      <article className="mx-auto max-w-2xl px-5 md:px-0 py-14 md:py-20 text-lg leading-[1.75] text-olive/85">
        {plan.blocks.map((b, i) => {
          if (b.type === "lead")
            return <Reveal key={i}><p className="my-8 font-serif text-2xl md:text-3xl text-olive-deep leading-snug">{b.text}</p></Reveal>;
          if (b.type === "pull")
            return (
              <Reveal key={i}>
                <blockquote className="my-12 border-l-2 border-terra pl-8 font-serif italic text-2xl md:text-3xl leading-snug text-olive-deep">{b.text}</blockquote>
              </Reveal>
            );
          if (b.type === "script")
            return <Reveal key={i}><p className="mt-10 mb-2 font-script text-4xl md:text-5xl text-terra">{b.text}</p></Reveal>;
          return <Reveal key={i}><p className="mb-6">{b.text}</p></Reveal>;
        })}

        {plan.closing.length > 0 && (
          <Reveal>
            <div className="mt-14 text-center">
              {plan.closing.map((line, i) => (
                <p key={i} className="font-serif text-2xl md:text-3xl uppercase tracking-[0.04em] text-terra leading-tight">{line}</p>
              ))}
            </div>
          </Reveal>
        )}
      </article>

      {details && (
        <section className="px-5 md:px-10 pb-4">
          <Reveal className="mx-auto max-w-4xl">
            <div className="rounded-2xl border border-olive/12 bg-cream-deep/30 p-7 md:p-10" data-testid="plan-details">
              <h2 className="font-serif text-2xl md:text-3xl text-olive-deep">Dette får du</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-x-10 gap-y-3">
                <ul className="space-y-2.5">
                  {details.contents.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-olive/85">
                      <Check size={18} className="mt-0.5 text-terra shrink-0" /> <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <dl className="space-y-3 text-[15px]">
                  <div className="flex items-start gap-2.5"><FileText size={17} className="mt-0.5 text-olive/60 shrink-0" /><span><span className="font-semibold text-olive-deep">Format:</span> {details.format} · {details.length}</span></div>
                  <div className="flex items-start gap-2.5"><Monitor size={17} className="mt-0.5 text-olive/60 shrink-0" /><span><span className="font-semibold text-olive-deep">Kompatibilitet:</span> {details.compatibility}</span></div>
                  <div className="flex items-start gap-2.5"><Clock size={17} className="mt-0.5 text-olive/60 shrink-0" /><span><span className="font-semibold text-olive-deep">Levering:</span> {details.delivery}</span></div>
                  <div className="flex items-start gap-2.5"><RefreshCw size={17} className="mt-0.5 text-olive/60 shrink-0" /><span><span className="font-semibold text-olive-deep">Oppdateringer:</span> {details.updates}</span></div>
                </dl>
              </div>
              <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-olive/12 pt-6">
                <div>
                  <p className="font-serif text-3xl text-terra" data-testid="plan-price">{plan.price}</p>
                  <p className="text-[12px] uppercase tracking-[0.14em] text-olive/50">{plan.free ? "Gratis · ingen MVA" : "Endelig pris · ingen MVA"}</p>
                </div>
                <Cta plan={plan} testid="plan-buy-details" freeToken={freeToken} setFreeToken={setFreeToken} />
              </div>
              <p className="mt-5 flex items-start gap-2.5 text-[13px] text-olive/60">
                <ShieldCheck size={16} className="mt-0.5 text-olive/50 shrink-0" /> {details.terms}
              </p>
            </div>
          </Reveal>
        </section>
      )}

      <section className="bg-[#4A5138] text-cream py-16 px-5 text-center">
        <Reveal>
          <p className="font-script text-3xl text-sage">Klar til å begynne?</p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">Få tilgang til {plan.name.toLowerCase()}.</h2>
          <div className="mt-8 flex justify-center">
            <Cta plan={plan} testid="plan-buy-bottom" freeToken={freeToken} setFreeToken={setFreeToken} />
          </div>
        </Reveal>
      </section>

      <div className="bg-terra text-cream text-center py-6 px-5">
        <p className="font-script text-2xl text-cream/90">La oss gjøre det – sammen.</p>
      </div>
    </div>
  );
}
